import LinkIcon from '@heroicons/react/outline/LinkIcon'
import MenuAlt1Icon from '@heroicons/react/outline/MenuAlt1Icon'
import MenuAlt2Icon from '@heroicons/react/outline/MenuAlt2Icon'
import NewspaperIcon from '@heroicons/react/outline/NewspaperIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'

import type { I18nShape } from '@tribeplatform/react-components/i18n'

import { StaticFieldTypes } from '../Views/types'
import type { StaticField } from '../Views/types'
import type {
  QuickLinksBlockProps,
  QuickLinksSource,
  QuickLinksView,
} from './types'

export const genericSourceFactory: (
  intl: I18nShape,
) => QuickLinksSource = intl => ({
  id: 'genericSource',
  title: intl.$t({
    defaultMessage: 'Title',
    id: 'Generics.Title',
  }),
  description: intl.$t({
    defaultMessage: 'Description',
    id: 'Generics.Description',
  }),
  linkText: 'Quick link',
  linkUrl: '#',
})

export const genericViewFactory: (
  intl: I18nShape,
) => QuickLinksView = intl => ({
  id: 'genericView',
  name: intl.$t({
    defaultMessage: 'Default',
    id: 'Generics.Default',
  }),
  style: 'list',
  gallerySize: 'medium',
  showDivider: true,
  sources: [genericSourceFactory(intl)],
  fields: [],
})

export const staticFieldsFactory: (intl: I18nShape) => StaticField[] = intl => [
  {
    id: 'quickLinkListImage',
    label: intl.$t({
      defaultMessage: 'Image',
      id: 'QuickLinksBlocks.FieldLabels.Image',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    type: StaticFieldTypes.avatar,
    settings: {
      avatarSize: 'lg',
      bannerSource: 'member',
    },
    isStatic: true,
    icon: <PhotographIcon />,
  },
  {
    id: 'quickLinkGalleryBanner',
    label: intl.$t({
      defaultMessage: 'Image',
      id: 'QuickLinksBlocks.FieldLabels.Image',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'post',
      aspectRatio: 'aspect-video',
    },
    isStatic: true,
    icon: <PhotographIcon />,
    default: '/images/banners/banner-placeholder.jpg',
  },
  {
    id: 'quickLinkCardBanner',
    label: intl.$t({
      defaultMessage: 'Image',
      id: 'QuickLinksBlocks.FieldLabels.Image',
    }),
    enabled: true,
    locked: true,
    styles: ['card'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'post',
      aspectRatio: 'aspect-[4/1]',
    },
    isStatic: true,
    icon: <PhotographIcon />,
    default: '/images/banners/banner-placeholder.jpg',
  },
  {
    id: 'quickLinkListContent',
    label: intl.$t({
      defaultMessage: 'Content',
      id: 'QuickLinksBlocks.FieldLabels.Content',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    settings: {
      stretchX: true,
    },
    isStatic: true,
    icon: <NewspaperIcon />,
    fields: [
      {
        id: 'quickLinkListTitle',
        label: intl.$t({
          defaultMessage: 'Title',
          id: 'QuickLinksBlocks.FieldLabels.Title',
        }),
        enabled: true,
        locked: false,
        type: StaticFieldTypes.title,
        settings: {
          titleSize: 'md',
        },
        isStatic: true,
        icon: <MenuAlt2Icon />,
      },
      {
        id: 'quickLinkListDescription',
        label: intl.$t({
          defaultMessage: 'Description',
          id: 'QuickLinksBlocks.FieldLabels.Description',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <MenuAlt1Icon />,
      },
    ],
  },
  {
    id: 'quickLinkGalleryTitle',
    label: intl.$t({
      defaultMessage: 'Title',
      id: 'QuickLinksBlocks.FieldLabels.Title',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel', 'card'],
    type: StaticFieldTypes.title,
    settings: {
      fullWidth: true,
      titleSize: 'md',
    },
    isStatic: true,
    icon: <MenuAlt2Icon />,
  },
  {
    id: 'quickLinkGalleryDescription',
    label: intl.$t({
      defaultMessage: 'Description',
      id: 'QuickLinksBlocks.FieldLabels.Description',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
  },
  {
    id: 'quickLinkCardDescription',
    label: intl.$t({
      defaultMessage: 'Description',
      id: 'QuickLinksBlocks.FieldLabels.Description',
    }),
    enabled: true,
    locked: false,
    styles: ['card'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
  },
  {
    id: 'quickLinkButton',
    label: intl.$t({
      defaultMessage: 'Link',
      id: 'QuickLinksBlocks.FieldLabels.Link',
    }),
    enabled: true,
    locked: false,
    type: StaticFieldTypes.button,
    settings: { linkStyle: 'button', openInNewWindow: true },
    isStatic: true,
    icon: <LinkIcon />,
  },
]

export const QuickLinksConfig: (intl: I18nShape) => {
  defaultProps: QuickLinksBlockProps
  displayName: string
  removable: boolean
  Icon: JSX.Element
} = ({ $t }) => ({
  defaultProps: {
    views: [],
    title: '',
    description: '',
    linkText: '',
    linkUrl: '',
  },
  displayName: $t({
    defaultMessage: 'Quick links',
    id: 'Blocks.QuickLinks.DisplayName',
  }),
  removable: true,
  Icon: <LinkIcon />,
})
